import {
  type Answers,
  type ElectorRecord,
  type Outcome,
  type Party,
  type VoterStatus,
} from "./domain";

export type Edit = Answers & { readonly notes: string };
export interface Draft extends Edit {
  readonly elector_id: string;
  readonly saved: ElectorRecord;
  readonly revision: number;
  readonly savedRevision: number;
  readonly saving: boolean;
  readonly error: unknown;
}

interface Entry {
  value: Draft;
  timer: ReturnType<typeof setTimeout> | null;
  pending: Promise<void> | null;
  enqueuedRevision: number;
}

export function complete(value: Edit): boolean {
  return (
    (value.general_outcome === "other_party") === (value.general_party !== null) &&
    (value.local_outcome === "other_party") === (value.local_party !== null)
  );
}
export function dirty(value: Draft): boolean {
  return value.revision !== value.savedRevision;
}

/** In-memory project state survives tab and session changes, but never access revocation. */
export class ProjectDrafts {
  private readonly entries = new Map<string, Entry>();
  private readonly listeners = new Set<() => void>();
  private readonly abort = new AbortController();
  private queue: Promise<void> = Promise.resolve();
  public constructor(
    private readonly persist: (
      id: string,
      edit: Edit,
      signal: AbortSignal,
    ) => Promise<ElectorRecord>,
  ) {}

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  public get all(): readonly Draft[] {
    return [...this.entries.values()].map(({ value }) => value);
  }
  public get pending(): readonly Draft[] {
    return this.all.filter(dirty);
  }
  public get(id: string): Draft | undefined {
    return this.entries.get(id)?.value;
  }
  public load(record: ElectorRecord): Draft {
    this.abort.signal.throwIfAborted();
    const existing = this.entries.get(record.elector_id);
    if (existing && dirty(existing.value)) return existing.value;
    const value: Draft = {
      ...record,
      saved: record,
      revision: 0,
      savedRevision: 0,
      saving: false,
      error: null,
    };
    this.entries.set(record.elector_id, {
      value,
      timer: null,
      pending: null,
      enqueuedRevision: -1,
    });
    return value;
  }
  public outcome(id: string, election: "general" | "local", outcome: Outcome) {
    const draft = this.require(id).value;
    this.edit(id, {
      [`${election}_outcome`]: draft[`${election}_outcome`] === outcome ? null : outcome,
      [`${election}_party`]: null,
    });
  }
  public party(id: string, election: "general" | "local", party: Party) {
    if (this.require(id).value[`${election}_outcome`] === "other_party")
      this.edit(id, { [`${election}_party`]: party });
  }
  public status(id: string, status: VoterStatus) {
    this.edit(id, {
      voter_status: this.require(id).value.voter_status === status ? null : status,
    });
  }
  public notes(id: string, notes: string) {
    this.edit(id, { notes }, true);
  }

  private edit(id: string, change: Partial<Edit>, debounce = false) {
    const entry = this.require(id);
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.value = {
      ...entry.value,
      ...change,
      revision: entry.value.revision + 1,
      error: null,
    };
    entry.timer = null;
    if (debounce) entry.timer = setTimeout(() => void this.save(id), 600);
    else if (complete(entry.value)) void this.save(id);
    this.notify();
  }

  public save(id: string): Promise<void> {
    if (this.abort.signal.aborted) return Promise.resolve();
    const entry = this.require(id);
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.timer = null;
    const current = entry.value;
    if (!dirty(current) || !complete(current)) return Promise.resolve();
    if (entry.enqueuedRevision === current.revision && entry.pending !== null)
      return entry.pending;
    const revision = current.revision;
    const edit: Edit = {
      general_outcome: current.general_outcome,
      general_party: current.general_party,
      local_outcome: current.local_outcome,
      local_party: current.local_party,
      voter_status: current.voter_status,
      notes: current.notes,
    };
    entry.enqueuedRevision = revision;
    entry.value = { ...entry.value, saving: true, error: null };
    const operation = this.queue
      .then(async () => {
        if (this.abort.signal.aborted) return;
        let record: ElectorRecord;
        try {
          record = await this.persist(id, edit, this.abort.signal);
        } catch (error) {
          if (!this.abort.signal.aborted && entry.value.revision === revision)
            entry.value = { ...entry.value, error };
          return;
        } finally {
          if (!this.abort.signal.aborted && entry.enqueuedRevision === revision) {
            entry.pending = null;
            entry.value = { ...entry.value, saving: false };
          }
        }
        if (!this.abort.signal.aborted)
          entry.value = { ...entry.value, saved: record, savedRevision: revision };
      })
      .finally(() => {
        if (!this.abort.signal.aborted) this.notify();
      });
    entry.pending = operation;
    this.queue = operation;
    this.notify();
    return operation;
  }
  public flush(): Promise<void[]> {
    return Promise.all(this.pending.map((entry) => this.save(entry.elector_id)));
  }
  public get settled(): Promise<void> {
    return this.queue;
  }
  public dispose(): void {
    this.abort.abort(new DOMException("Project access ended.", "AbortError"));
    for (const entry of this.entries.values())
      if (entry.timer !== null) clearTimeout(entry.timer);
    this.entries.clear();
    this.listeners.clear();
  }
  private require(id: string): Entry {
    this.abort.signal.throwIfAborted();
    const entry = this.entries.get(id);
    if (!entry) throw new Error("Load this voter's result before editing.");
    return entry;
  }
  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}
